/**
 * Fee policy form ↔ wire owner (design-patient-payment-guidance §4.13.4, D5, D11, D24).
 *
 * The form holds dollar strings; the wire holds integer cents. This module is the only place that
 * converts between them and the only place that maps the server's `payment_policy.<field>` error
 * keys onto the form's `feePolicy.<field>` keys. The generator twin (`utils/paymentGuidance.ts`)
 * knows nothing about forms.
 *
 * @module utils/feePolicy
 */

import type { BillingMethod, PaymentPolicy } from '@/types/paymentGuidance';

export interface FeePolicyFields {
  initialFee: string;
  followUpFee: string;
  lateFee: string;
  noticeHours: string;
  notes: string;
  billingMethod: BillingMethod | '';
  medicareApplies: boolean;
}

export const EMPTY_FEE_POLICY: FeePolicyFields = {
  initialFee: '',
  followUpFee: '',
  lateFee: '',
  noticeHours: '',
  notes: '',
  billingMethod: '',
  medicareApplies: true,
};

/** Digits with at most two decimals — the same rule as the admin form's `_MONEY_RE` (D5). */
export const MONEY_RE = /^\d+(\.\d{1,2})?$/;

/** Trim and strip a leading `$`; the shared normalisation for `dollarsToCents` and `validateMoney`. */
export const normaliseMoneyText = (text: string): string => text.trim().replace(/^\$/, '').trim();

/**
 * '' → null; '120.50' → 12050; anything not matching MONEY_RE → NaN.
 * Converts by string arithmetic, never `Number(text) * 100` (`Number('1.15') * 100` is 114.99999999999999).
 */
export function dollarsToCents(text: string): number | null {
  const normalised = normaliseMoneyText(text);
  if (normalised === '') return null;
  if (!MONEY_RE.test(normalised)) return NaN;
  const [whole, frac] = normalised.split('.');
  return Number(whole) * 100 + Number((frac ?? '').padEnd(2, '0'));
}

/** 12050 → '120.50'; 12000 → '120'; 5 → '0.05'; null → ''. Integer arithmetic only. */
export function centsToDollars(cents: number | null): string {
  if (cents === null) return '';
  const whole = Math.floor(cents / 100);
  const rem = cents % 100;
  return rem === 0 ? String(whole) : `${whole}.${String(rem).padStart(2, '0')}`;
}

const centsOrNull = (text: string): number | null => {
  const cents = dollarsToCents(text);
  return cents === null || Number.isNaN(cents) ? null : cents;
};

/** Form → wire. Invalid numbers become null so the preview reads as if the field were blank (§4.13.4). */
export function feeFieldsToPolicy(fields: FeePolicyFields): PaymentPolicy {
  const hours = fields.noticeHours.trim();
  const notes = fields.notes.trim();
  return {
    fee_initial_cents: centsOrNull(fields.initialFee),
    fee_followup_cents: centsOrNull(fields.followUpFee),
    billing_method: fields.billingMethod === '' ? null : fields.billingMethod,
    accepts_medicare_ccm: fields.medicareApplies,
    cancellation_notice_hours: /^\d+$/.test(hours) ? Number(hours) : null,
    cancellation_fee_cents: centsOrNull(fields.lateFee),
    payment_notes: notes === '' ? null : notes,
  };
}

/** Wire → form. `null` (no stored policy) hydrates the empty section. */
export function policyToFeeFields(policy: PaymentPolicy | null): FeePolicyFields {
  if (!policy) return { ...EMPTY_FEE_POLICY };
  return {
    initialFee: centsToDollars(policy.fee_initial_cents),
    followUpFee: centsToDollars(policy.fee_followup_cents),
    lateFee: centsToDollars(policy.cancellation_fee_cents),
    noticeHours: policy.cancellation_notice_hours === null ? '' : String(policy.cancellation_notice_hours),
    notes: policy.payment_notes ?? '',
    billingMethod: policy.billing_method ?? '',
    medicareApplies: policy.accepts_medicare_ccm,
  };
}

/** Blank means "no policy": every text field empty, no method, Medicare at its default (D6). */
export function isBlankFeeFields(fields: FeePolicyFields): boolean {
  return (
    fields.initialFee.trim() === '' &&
    fields.followUpFee.trim() === '' &&
    fields.lateFee.trim() === '' &&
    fields.noticeHours.trim() === '' &&
    fields.notes.trim() === '' &&
    fields.billingMethod === '' &&
    fields.medicareApplies === true
  );
}

const SERVER_TO_FORM: Record<string, string> = {
  'payment_policy.fee_initial_cents': 'feePolicy.initialFee',
  'payment_policy.fee_followup_cents': 'feePolicy.followUpFee',
  'payment_policy.cancellation_fee_cents': 'feePolicy.lateFee',
  'payment_policy.cancellation_notice_hours': 'feePolicy.noticeHours',
  'payment_policy.payment_notes': 'feePolicy.notes',
  'payment_policy.billing_method': 'feePolicy.billingMethod',
  'payment_policy.accepts_medicare_ccm': 'feePolicy.medicareApplies',
};

/** Turns the server's `payment_policy.<field>` keys (ApiClient §4.13.6) into the form's `feePolicy.<field>` keys; other keys pass through. */
export function mapServerFieldErrors(errors: Record<string, string>): Record<string, string> {
  const mapped: Record<string, string> = {};
  for (const [key, message] of Object.entries(errors)) {
    mapped[SERVER_TO_FORM[key] ?? key] = message;
  }
  return mapped;
}
